if (!customElements.get('sticky-atc-panel')) {
  // Anything in the form that adding from the panel would silently skip —
  // Evolve/Cima sets, Cima GR's seatpost, Avenger fit, crank config, Zephyr
  // spacers, the spacer service, Pardus pickup, gift-card recipients and
  // personalization all surface as one of these.
  const ADDON_SELECTOR = '.raceit-addon, .set-addons, .addon-btn, [data-raceit-addon-stack], [name^="properties["]';
  // Controls whose use counts as "the shopper has started configuring".
  const CONFIG_SELECTOR = '.product-options, raceit-product-options, .raceit-addon, .set-addons, .addon-btn, [data-raceit-addon-stack]';

  class StickyAtcPanel extends HTMLElement {
    constructor() {
      super();

      this.productSection = this.closest('.cc-main-product');
      this.productInfo = this.productSection.querySelector('.product-info');
      this.root = document.documentElement;

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }

      this.watchConfiguration();
      this.upgradeAddButton();
      this.addEventListener('click', this.handleDockClick.bind(this));
    }

    /**
     * Remembers on the section (which survives variant re-renders) that the
     * shopper has touched an option or add-on. Until then the dock only leads
     * to the options: with the first variant preselected, adding from the dock
     * straight away could put a size nobody chose in the cart.
     */
    watchConfiguration() {
      const section = this.productSection;
      if (section.dataset.rcConfigWatch) return;
      section.dataset.rcConfigWatch = 'true';
      const mark = (evt) => {
        if (evt.target.closest(CONFIG_SELECTOR)) section.dataset.rcConfigured = 'true';
      };
      section.addEventListener('change', mark);
      section.addEventListener('click', mark);
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      window.removeEventListener('scroll', this.onDockScroll);
      window.removeEventListener('resize', this.onDockScroll);
      document.removeEventListener('rc:consent-change', this.onDockScroll);
      // A variant change swaps this element for a fresh copy; the new one
      // re-reads the state straight away, so only clear it when truly gone.
      window.requestAnimationFrame(() => {
        if (!document.querySelector('sticky-atc-panel')) this.root.classList.remove('rc-dock-on');
      });
    }

    bindEvents() {
      const observer = new IntersectionObserver((entries) => {
        if (!theme.mediaMatches.md) return;
        entries.forEach((entry) => {
          if (entry.target === this.productInfo) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
            this.syncDock();
          }
        });
      });

      if (this.productInfo) observer.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);

      // Mobile: the add button sits 3-6 screens down on bikes and frames, and
      // most phone visitors never scroll that far, so waiting for it to leave
      // the screen (the old rule) meant the panel almost never showed. It now
      // floats in as soon as the price has scrolled away and only steps aside
      // while the real add button is on screen.
      let ticking = false;
      this.onDockScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(() => {
          ticking = false;
          this.syncDock();
        });
      };
      window.addEventListener('scroll', this.onDockScroll, { passive: true });
      window.addEventListener('resize', this.onDockScroll, { passive: true });
      document.addEventListener('rc:consent-change', this.onDockScroll);

      // Variant changes replace this element: skip the entrance animation
      // for the replacement so the dock does not blink.
      this.style.transition = 'none';
      this.syncDock();
      window.requestAnimationFrame(() => window.requestAnimationFrame(() => { this.style.transition = ''; }));
    }

    /**
     * The section re-renders parts of itself on every variant change, so the
     * pieces are looked up fresh each time rather than cached.
     */
    parts() {
      const form = this.productSection.querySelector('.js-product-form-main');
      return {
        price: this.productSection.querySelector('.product-price'),
        picker: this.productSection.querySelector('.product-options'),
        form,
        addBtn: form && form.querySelector('[name="add"]'),
        addArea: this.productSection.querySelector('.product-info__add-to-cart') || form,
      };
    }

    canAddFromDock({ form, addBtn }) {
      return Boolean(
        form && addBtn && !addBtn.disabled
        && !this.productSection.querySelector(ADDON_SELECTOR)
        && form.checkValidity()
      );
    }

    syncDock() {
      if (theme.mediaMatches.md) {
        // Desktop shows the panel from the observer above; only report it, so
        // the corner controls can step above it.
        this.setDockState(!this.classList.contains('sticky-atc-panel--out') && !document.body.classList.contains('scrolled-to-bottom'));
        return;
      }

      const p = this.parts();
      const viewH = window.innerHeight;
      const priceGone = p.price ? p.price.getBoundingClientRect().bottom < 0 : window.scrollY > viewH * 0.6;
      const area = p.addArea && p.addArea.getBoundingClientRect();
      const addInView = Boolean(area && area.top < viewH && area.bottom > 0);
      const show = priceGone && !addInView && !document.body.classList.contains('scrolled-to-bottom');

      // Until the shopper has touched the options the dock leads to them;
      // after that it adds straight away, or leads to the add button when an
      // add-on still needs an answer. Products without options add directly.
      const configured = !p.picker || this.productSection.dataset.rcConfigured === 'true';
      let mode = 'review';
      if (!configured) mode = 'choose';
      else if (this.canAddFromDock(p)) mode = 'add';
      this.setMode(mode);

      this.classList.toggle('sticky-atc-panel--out', !show);
      this.setDockState(show);
    }

    setMode(mode) {
      if (this.dataset.mode === mode) return;
      this.dataset.mode = mode;
      const text = this.dataset[`label${mode.charAt(0).toUpperCase()}${mode.slice(1)}`];
      const label = this.querySelector('.sticky-atc-panel__cta-label');
      if (label && text) label.textContent = text;
    }

    setDockState(visible) {
      if (visible === this.dockVisible) return;
      this.dockVisible = visible;
      this.inert = !visible && !theme.mediaMatches.md;
      this.root.classList.toggle('rc-dock-on', visible);
      if (visible) {
        this.root.style.setProperty('--rc-dock-h', `${Math.round(this.offsetHeight)}px`);
      }
    }

    handleDockClick(evt) {
      const link = evt.target.closest('.sticky-atc-panel__cta');
      if (!link || evt.defaultPrevented) return;

      const p = this.parts();
      let target = document.getElementById((link.getAttribute('href') || '').replace('#', ''));
      let block = 'start';

      if (!theme.mediaMatches.md) {
        if (this.dataset.mode === 'add' && this.canAddFromDock(p)) {
          evt.preventDefault();
          p.addBtn.click();
          return;
        }
        if (this.dataset.mode !== 'choose') {
          target = p.addArea;
          block = 'center';
        } else {
          target = document.getElementById('variants') || p.picker || target;
        }
      }

      if (!target) return;
      evt.preventDefault();
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block });
    }

    /**
     * Upgrades the single-variant panel link into a real add-to-cart.
     *
     * The link ships as an anchor back to the product info (the no-JS
     * fallback, and the correct behaviour for anything with a choice
     * still to make). It only becomes an add-to-cart when this product's
     * form turns out to be genuinely simple. On mobile the dock modes in
     * syncDock() take over the label.
     */
    upgradeAddButton() {
      const link = this.querySelector('.js-sticky-atc-add');
      if (!link) return;

      const { form, addBtn } = this.parts();
      if (!form || !addBtn) return;
      if (this.productSection.querySelector(ADDON_SELECTOR)) return;

      // Catches required controls the selector above would miss.
      if (!form.checkValidity()) return;

      const addText = link.dataset.addToCartText;
      const label = link.querySelector('.sticky-atc-panel__cta-label') || link;
      if (addText && theme.mediaMatches.md) label.textContent = addText;

      link.addEventListener('click', (evt) => {
        // Re-check at click time: stock and validity can change while the
        // shopper is on the page. Falling through keeps the old scroll-up
        // behaviour as the safe default.
        if (addBtn.disabled || !form.checkValidity()) return;

        evt.preventDefault();
        addBtn.click();
      });
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
